import i18n from 'src/i18n'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { AVAILABILITY_REASONS, useAvailabilityResult } from 'src/modules/checkout/composables/sales-availability/AvailabilityResult'
import { useIsModifierAvailable } from 'src/modules/checkout/composables/sales-availability/modifiers/IsModifierAvailable'
import { toValue } from 'vue'

// Options is a hash containing fields that can be used to validate modifier set availability.
const DEFAULT_OPTIONS = Object.freeze({
  salesChannel: null,
  orderItem: null
})

export function useIsItemModifierSetAvailable () {
  const { result } = useAvailabilityResult()
  const { isModifierAvailable } = useIsModifierAvailable()

  function isItemModifierSetAvailable (itemModifierSet, { salesChannel, orderItem } = DEFAULT_OPTIONS) {
    const itemModifierSetValue = toValue(itemModifierSet)

    if (_.isNil(itemModifierSetValue)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_NIL,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NIL}`)
      })
    }

    const salesChannelValue = toValue(salesChannel)
    const orderItemValue = toValue(orderItem)

    // The Item Modifier Set's Modifier Set must be active (not archived) to be presented
    if (itemModifierSetValue.modifierSet.archivable && !itemModifierSetValue.modifierSet.isActive) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_ARCHIVED,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_ARCHIVED}`)
      })
    }

    // The Item Modifier Set's Modifier Set must be available on the current Sales Channel
    if (!itemModifierSetValue.modifierSet.isSalesChannelEnabled(salesChannelValue)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_DISABLED_ON_SALES_CHANNEL,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_DISABLED_ON_SALES_CHANNEL}`, { salesChannel: _.startCase(salesChannelValue) })
      })
    }

    // If the Sales Channel is private, the Item Modifier Set is available, regardless of
    // Inventory or Sales Toggle (the Item Modifier Set may be shown without being selectable)
    if (_.includes(SALES_CHANNEL_TYPES.PRIVATE, salesChannelValue)) {
      return result({
        isAvailable: true
      })
    }

    // If the Item Modifier Set has no minimum selection count, it is available
    if (!_.isInteger(itemModifierSetValue.minSelectionCount) || itemModifierSetValue.minSelectionCount === 0) {
      return result({
        isAvailable: true
      })
    }

    let availableModifierCount = 0
    const options = {
      salesChannel: salesChannelValue,
      orderItem: orderItemValue
    }

    for (const modifier of itemModifierSetValue.modifierSet.modifiers.data) {
      if (isModifierAvailable(modifier, options).isAvailable) {
        availableModifierCount++
      }

      if (availableModifierCount >= itemModifierSetValue.minSelectionCount) {
        break
      }
    }

    if (availableModifierCount < itemModifierSetValue.minSelectionCount) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_SELECTION_REQUIREMENTS_UNMET,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_SELECTION_REQUIREMENTS_UNMET}`)
      })
    }

    return result({
      isAvailable: true
    })
  }

  return {
    isItemModifierSetAvailable
  }
}
